import { useReducer } from 'react';
import { useDispatch } from 'react-redux';

import { SellerSettings } from '../../../../enums/seller-settings/seller-settings';
import {
  useInventoryReOrderThreshold,
  useInventoryShipNoStock,
  useInventoryShipNoWarehouse,
  useInventoryShipUnknown,
  useInventoryTrackingEnabled,
} from '../../../../hooks/use-seller-setting';
import { saveNewSellerSettings } from '../../../../store/seller/seller.actions';
import { updateSellerSettingsBulk } from '../../api/save-settings';
import { notifications } from '../../components/notifications';

/**
 * @typedef  {{inventoryShipNoStock: "allow" | "warn" | "error",
 * inventoryShipUnknown: "allow" | "warn" | "error",
 * inventoryReOrderThreshold: number,
 * inventoryShipNoWarehouse: "warn" | "doNotWarn",
 * inventoryTrackingEnabled: boolean  }} Configuration
 */

/**
 * @returns {Configuration} configuration
 */
const useInventoryConfiguration = () => {
  const inventoryShipNoStock = useInventoryShipNoStock();
  const inventoryShipUnknown = useInventoryShipUnknown();
  const inventoryReOrderThreshold = useInventoryReOrderThreshold();
  const inventoryShipNoWarehouse = useInventoryShipNoWarehouse();
  const inventoryTrackingEnabled = useInventoryTrackingEnabled();

  return {
    inventoryShipNoStock,
    inventoryShipUnknown,
    inventoryReOrderThreshold,
    inventoryShipNoWarehouse: inventoryShipNoWarehouse ? 'warn' : 'doNotWarn',
    inventoryTrackingEnabled,
  };
};

export const configurationActions = {
  setInventoryShipNoStock: 'setInventoryShipNoStock',
  setInventoryShipUnknown: 'setInventoryShipUnknown',
  setInventoryReOrderThreshold: 'setInventoryReOrderThreshold',
  setInventoryShipNoWarehouse: 'setInventoryShipNoWarehouse',
  setInventoryTrackingEnabled: 'setInventoryTrackingEnabled',
  toggleInventoryTrackingEnabled: 'toggleInventoryTrackingEnabled',
};

/**
 * @param {Configuration} state
 * @param {{type: string, value: any }} action
 * @returns {Configuration}
 */
const configurationReducer = (state, action) => {
  switch (action.type) {
    case configurationActions.setInventoryShipNoStock:
      return { ...state, inventoryShipNoStock: action.value };
    case configurationActions.setInventoryShipUnknown:
      return { ...state, inventoryShipUnknown: action.value };
    case configurationActions.setInventoryReOrderThreshold:
      return { ...state, inventoryReOrderThreshold: action.value };
    case configurationActions.setInventoryShipNoWarehouse:
      return { ...state, inventoryShipNoWarehouse: action.value };
    case configurationActions.setInventoryTrackingEnabled:
      return { ...state, inventoryTrackingEnabled: action.value };
    case configurationActions.toggleInventoryTrackingEnabled:
      return {
        ...state,
        inventoryTrackingEnabled: !state.inventoryTrackingEnabled,
      };
    default:
      return state;
  }
};

/**
 * Custom hook that returns the configuration and dispatch function for managing inventory settings.
 * @returns {{
 * onSave: (fn: () => void) => void,
 * onUpdate: (arg: { type: string, value: any }) => void,
 * onCancel:(fn: () => void) => void,
 * configuration: Configuration
 * originalConfiguration: Configuration}}
 */
export const useInventorySetup = () => {
  const originalConfiguration = useInventoryConfiguration();

  const [configuration, onUpdate] = useReducer(configurationReducer, originalConfiguration);

  const onCancel = () => {
    onUpdate({
      type: configurationActions.setInventoryShipNoStock,
      value: originalConfiguration.inventoryShipNoStock,
    });
    onUpdate({
      type: configurationActions.setInventoryShipUnknown,
      value: originalConfiguration.inventoryShipUnknown,
    });
    onUpdate({
      type: configurationActions.setInventoryReOrderThreshold,
      value: originalConfiguration.inventoryReOrderThreshold,
    });
    onUpdate({
      type: configurationActions.setInventoryShipNoWarehouse,
      value: originalConfiguration.inventoryShipNoWarehouse,
    });
    onUpdate({
      type: configurationActions.setInventoryTrackingEnabled,
      value: originalConfiguration.inventoryTrackingEnabled,
    });
  };

  const dispatch = useDispatch();

  /**
   * @param {() => void} onSuccess function to call when the save is successful
   */
  const onSave = async (onSuccess) => {
    try {
      const settings = {
        [SellerSettings.InventoryShipNoStock.key]: configuration.inventoryShipNoStock,
        [SellerSettings.InventoryShipUnknown.key]: configuration.inventoryShipUnknown,
        [SellerSettings.InventoryReOrderThreshold.key]: configuration.inventoryReOrderThreshold,
        [SellerSettings.InventoryShipNoWarehouse.key]: configuration.inventoryShipNoWarehouse === 'warn',
        [SellerSettings.InventoryTrackingEnabled.key]: configuration.inventoryTrackingEnabled,
      };
      const newSettings = await updateSellerSettingsBulk(settings);
      dispatch(saveNewSellerSettings(newSettings));

      notifications.success();
      onSuccess();
    } catch (e) {
      notifications.error(e);
    }
  };

  return {
    onSave,
    onCancel,
    onUpdate,
    configuration,
    originalConfiguration,
  };
};
